/**
 * Crumbs a page adds below its place in the nav: "CPT-3993 › Line 3 › S07".
 *
 * The top bar's breadcrumb is read from the nav config, which knows a page
 * belongs under "Concepts" but nothing about which concept, which line or
 * which shot - that is data only the page has, after it has loaded. So a page
 * that is deeper than "a detail of a list" pushes its own trail here with
 * `usePageCrumbs`, and the breadcrumb draws it after the nav's group and item
 * instead of the generic "Details". A page that pushes nothing is drawn
 * exactly as before; that is every page that existed before this one.
 *
 * The trail belongs to the page that pushed it: it is cleared when that page
 * unmounts, so the next page never inherits a crumb it did not set.
 */
import { createContext, use, useEffect, useMemo, useRef, useState, type ReactNode } from "react";

export interface PageCrumb {
  label: string;
  /** Where the crumb goes. Omitted on the last one, which is where the reader is. */
  to?: string;
}

interface PageCrumbsContextValue {
  crumbs: readonly PageCrumb[] | null;
  setCrumbs: (crumbs: readonly PageCrumb[] | null) => void;
}

const PageCrumbsContext = createContext<PageCrumbsContextValue | null>(null);

export function PageCrumbsProvider({ children }: { children: ReactNode }) {
  const [crumbs, setCrumbs] = useState<readonly PageCrumb[] | null>(null);
  const value = useMemo(() => ({ crumbs, setCrumbs }), [crumbs]);
  return <PageCrumbsContext value={value}>{children}</PageCrumbsContext>;
}

/**
 * Push this page's trail; null (or nothing yet, while the data loads) pushes
 * none. Compared by content, so a page may build the array inline on every
 * render without the breadcrumb re-rendering in a loop.
 */
export function usePageCrumbs(crumbs: readonly PageCrumb[] | null): void {
  const context = use(PageCrumbsContext);
  const setCrumbs = context?.setCrumbs;
  const latest = useRef(crumbs);
  latest.current = crumbs;
  const signature = crumbs === null ? null : JSON.stringify(crumbs);

  useEffect(() => {
    if (!setCrumbs) return;
    setCrumbs(latest.current);
    return () => setCrumbs(null);
  }, [setCrumbs, signature]);
}

/** The trail the current page pushed, for the breadcrumb. Null when it pushed none. */
export function usePushedCrumbs(): readonly PageCrumb[] | null {
  return use(PageCrumbsContext)?.crumbs ?? null;
}
